import { AxiosError } from 'axios';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { backendErrorNotification, frontendNotification } from '../../../../components/Notification';
import { NotificationTypes } from '../../../../components/Notification/notificationEnums';
import {
  ActionButtonsContainer,
  DeleteIconTable,
  EditIconTable,
} from '../../../../components/TablePaginator/styles';
import AsyncTable, { Column, RequestPage } from '../../../../components/AsyncTable';
import HttpStatus from '../../../../enums/httpStatus';
import i18n from '../../../../i18n';
import { RunIcon } from '../../../../icons';
import { audienceAPI, audienceAutomatedRunAPI } from '../../../../services/requests';
import ContentContainer from '../../../../templates/Content/ContentContainer';
import { Audience } from '../../../../types';
import { ApiResPaginated } from '../../../../types/apiResponseTypes';

import NewRegisterBtn from '../../../../components/Buttons/NewRegisterBtn';
import ModalCreate from './Components/ModalCreate/CreateAudienceModal';
import ModalDelete from './Components/ModalDelete/DeleteAudienceModal';
import ModalEdit from './Components/ModalEdit/EditAudienceModal';

const AudienceJSX = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [waitingReq, setWaitingReq] = useState(false);
  const [audiences, setAudiences] = useState<Audience[]>([]);
  const [selectedAudience, setSelectedAudience] = useState<Audience | null>(null);
  const [runningAudienceIds, setRunningAudienceIds] = useState<number[]>([]);
  const runningAudienceIdsRef = useRef(new Set<number>());

  const [showModalCreate, setShowModalCreate] = useState(false);
  const [showModalEdit, setShowModalEdit] = useState(false);
  const [showModalDelete, setShowModalDelete] = useState(false);
  const [updateTable, setUpdateTable] = useState(0);

  const getAudiences: RequestPage = async ({ search, page, quantityPerPage }) => {
    const source = audienceAPI.axios.CancelToken.source();

    try {
      audienceAPI.query =
        `?search=${encodeURIComponent(search)}&page=${page}&quantityPerPage=${quantityPerPage}`;
      const res = await audienceAPI.index(source.token);

      if (res.status === HttpStatus.OK) {
        const { data, paginated }: ApiResPaginated<Audience[]> = res.data;

        return {
          data: data ?? [],
          hasMore: (data ?? []).length > 0,
          totalPage: paginated?.totalPage ?? 0,
        };
      } else {
        throw res;
      }
    } catch (err) {
      if (!audienceAPI.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }

      return {
        data: [],
        hasMore: false,
        totalPage: 0,
      };
    } finally {
      audienceAPI.query = '';
    }
  };

  const refreshTable = () => setUpdateTable((currentUpdate) => currentUpdate + 1);

  const handleTableEditBtn = async (row: Audience) => {
    if (row.isAutomated) {
      setSelectedAudience(row);
      setShowModalEdit(true);
      return;
    }

    setWaitingReq(true);
    audienceAPI.query = '?withParticipants=true';
    const source = audienceAPI.axios.CancelToken.source();

    try {
      const res = await audienceAPI.show(row.id as number, source.token);

      if (res.status === HttpStatus.OK) {
        setSelectedAudience(res.data?.data as Audience);
        setShowModalEdit(true);
      } else {
        throw res;
      }
    } catch (err) {
      if (!audienceAPI.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
    } finally {
      audienceAPI.query = '';
      setWaitingReq(false);
    }
  };
  const handleTableDeleteBtn = (row: Audience) => {
    setSelectedAudience(row);
    setShowModalDelete(true);
  };

  const handleRunAutomatedAudience = async (row: Audience) => {
    const audienceId = row.id as number;

    if (!row.isAutomated || runningAudienceIdsRef.current.has(audienceId)) {
      return;
    }

    runningAudienceIdsRef.current.add(audienceId);
    setRunningAudienceIds((currentAudienceIds) => [...currentAudienceIds, audienceId]);
    audienceAutomatedRunAPI.query = `/${audienceId}/run`;

    try {
      const res = await audienceAutomatedRunAPI.post({});

      if (res.status === HttpStatus.OK) {
        frontendNotification({
          message: t('automatedAudienceRunStarted'),
          type: NotificationTypes.SUCCESS,
        });
      } else {
        throw res;
      }
    } catch (err) {
      if (!audienceAutomatedRunAPI.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
    } finally {
      audienceAutomatedRunAPI.query = '';
      runningAudienceIdsRef.current.delete(audienceId);
      setRunningAudienceIds((currentAudienceIds) =>
        currentAudienceIds.filter((currentAudienceId) => currentAudienceId !== audienceId),
      );
    }
  };

  const actionsButtons = (row: Audience) => (
    <ActionButtonsContainer>
      {row.isAutomated && (
        <RunIcon
          size="15px"
          color={theme.colors.text}
          style={{
            margin: '0 1rem',
            color: theme.colors.text,
            fill: theme.colors.text,
          }}
          title={t('runAutomatedAudience')}
          onClick={() => handleRunAutomatedAudience(row)}
          disabled={runningAudienceIds.includes(row.id as number)}
        />
      )}
      <EditIconTable onClick={() => handleTableEditBtn(row)} />
      <DeleteIconTable onClick={() => handleTableDeleteBtn(row)} />
    </ActionButtonsContainer>
  );

  const columns: Column<Audience>[] = useMemo(
    () => [
      {
        label: t('id') as string,
        accessor: 'id',
      },
      {
        label: t('description') as string,
        accessor: 'description',
      },
      {
        label: t('sellers') as string,
        Cell: (row: Audience) => String(row.participantCounts?.seller ?? 0),
      },
      {
        label: t('customers') as string,
        Cell: (row: Audience) => String(row.participantCounts?.customer ?? 0),
      },
      {
        label: t('users') as string,
        Cell: (row: Audience) => String(row.participantCounts?.user ?? 0),
      },
      {
        label: t('automated') as string,
        Cell: (row: Audience) => (
          <strong
            style={{
              color: row.isAutomated ? theme.colors.positive : theme.colors.negative,
            }}
          >
            {row.isAutomated ? t('yes') : t('no')}
          </strong>
        ),
      },
      {
        label: '',
        Cell: actionsButtons,
      },
    ],
    [i18n.language, theme, runningAudienceIds],
  );

  return (
    <ContentContainer title={t('audiences')}>
      <NewRegisterBtn
        onClick={() => setShowModalCreate(true)}
        disabled={waitingReq}
        style={{ marginLeft: '0', marginBottom: '1rem' }}
      />

      {showModalCreate && (
        <ModalCreate
          closeModal={() => setShowModalCreate(false)}
          callback={refreshTable}
          allowAutomated
        />
      )}

      {showModalDelete && (
        <ModalDelete
          selectedItem={selectedAudience as Audience}
          closeModal={() => setShowModalDelete(false)}
          callback={refreshTable}
        />
      )}

      {showModalEdit && (
        <ModalEdit
          selectedItem={selectedAudience as Audience}
          closeModal={() => setShowModalEdit(false)}
          callback={refreshTable}
          onParticipantsChanged={refreshTable}
          allowAutomated
        />
      )}

      <AsyncTable
        tableName={t('audiences')}
        columns={columns}
        value={audiences}
        onChange={setAudiences}
        requestPage={getAudiences}
        reqListener={[updateTable]}
        options={{
          hideDownloadCSV: true,
          hideDownloadXLS: true,
          hideDownloadPDF: true,
          hideDownloadLabel: true,
          styles: {
            primaryColor: `${theme.colors.surface}`,
            secondaryColor: `${theme.colors.onSurface}`,
            alternateRowColor: theme.colors.onSurfaceLight,
            textColor: theme.colors.text,
          },
          quantityPerPageLabel: t('quantityPerPage'),
          searchPlaceholder: t('search'),
        }}
      />
    </ContentContainer>
  );
};

export default AudienceJSX;
